// Avatar (009 T036): a reader's picture, or their initials when there is none or it does not load (revision 5).
import { cn } from '../lib/cn';
import { SafeImage } from './SafeImage';

/** Props of {@link Avatar}. */
export interface AvatarProps {
  name: string | null | undefined;
  src?: string | null;
  size?: number;
  className?: string;
}

/**
 * The initials of a name: first letters of the first and last words.
 * @param name a display name
 * @returns one or two upper-case letters, or an empty string
 */
export function initials(name: string | null | undefined): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '';
  const first = [...words[0]!][0] ?? '';
  const last = words.length > 1 ? ([...words[words.length - 1]!][0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

/**
 * An avatar.
 * @param props the name, optional image and size
 * @returns the avatar
 */
export function Avatar({ name, src, size = 48, className }: AvatarProps) {
  const style = { width: size, height: size };
  const letters = (
    <span
      role="img"
      aria-label={name ?? ''}
      style={style}
      className={cn(
        'inline-flex items-center justify-center rounded-full bg-primary font-display font-bold text-white',
        className,
      )}
    >
      <span aria-hidden="true">{initials(name)}</span>
    </span>
  );
  // A picture that does not load gives way to the initials (009 revision 5).
  if (src)
    return (
      <SafeImage
        src={src}
        alt={name ?? ''}
        style={style}
        className={cn('rounded-full object-cover', className)}
        fallback={letters}
      />
    );
  return letters;
}
